import { Anchor, Group, Paper, Text } from "@mantine/core";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import type { TeamDailyTicket } from "@msc/shared";

import { TicketRow } from "./TicketRow.js";

/**
 * Wie viele Eingangs-Vorgänge sofort stehen; der Rest per Aufklappen.
 *
 * Bewusst knapp: Die obere Reihe darf das Board nicht unter die Falz drücken —
 * die Ticketübersicht je Mitarbeiter ist das Wichtigste am Daily, der Eingang
 * nur ihr Vorlauf. Bei acht Zeilen war die Reihe 630 Pixel hoch und das Board
 * erst nach dem Scrollen zu sehen.
 */
const INTAKE_CAP = 5;

/**
 * Der **Eingang**: offene Vorgänge, die niemandem zugewiesen sind oder noch
 * nicht in Arbeit stehen — die Liste, über die ein Daily entscheidet. Steht
 * bewusst **über** den Personenkarten: Was noch niemandem gehört, geht unter,
 * sobald es neben den laufenden Tickets steht.
 *
 * Ältestes zuerst (so kommt es vom Server): Ein unzugewiesener Vorgang von
 * letzter Woche ist dringender als der von heute Morgen.
 */
export function IntakePanel({
  tickets,
  jiraBaseUrl,
}: {
  tickets: TeamDailyTicket[];
  jiraBaseUrl: string | null;
}) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const shown = expanded ? tickets : tickets.slice(0, INTAKE_CAP);
  const hidden = tickets.length - shown.length;

  return (
    <Paper withBorder p="sm" style={{ height: "100%" }}>
      <Group justify="space-between" gap={4} wrap="nowrap" mb={6}>
        <Text size="xs" fw={700} c="dimmed" tt="uppercase">
          {t("pages.teamDaily.intake")}
        </Text>
        {tickets.length > 0 && (
          <Text size="xs" c="dimmed">
            {t("pages.teamDaily.intakeCount", { count: tickets.length })}
          </Text>
        )}
      </Group>

      {tickets.length === 0 ? (
        <Text size="sm" c="dimmed">
          {t("pages.teamDaily.intakeEmpty")}
        </Text>
      ) : (
        <>
          {shown.map((ticket) => (
            <TicketRow
              key={ticket.key}
              ticket={ticket}
              jiraBaseUrl={jiraBaseUrl}
              zeigeBearbeiter
              zeigeAlter
            />
          ))}
          {hidden > 0 && (
            <Anchor
              component="button"
              type="button"
              size="xs"
              onClick={() => setExpanded(true)}
              mt={4}
            >
              {t("pages.teamDaily.moreTickets", { count: hidden })}
            </Anchor>
          )}
        </>
      )}
    </Paper>
  );
}
